/**
 * angleUtils.js — Client-side angle calculation utilities.
 *
 * Mirrors the backend cv_angle_utils.py logic so the UI can show
 * real-time feedback without waiting for the server round-trip.
 */

// Landmark indices (MediaPipe Pose Landmarker 33-point model)
export const LANDMARK = {
  NOSE: 0,
  LEFT_EYE_INNER: 1, LEFT_EYE: 2, LEFT_EYE_OUTER: 3,
  RIGHT_EYE_INNER: 4, RIGHT_EYE: 5, RIGHT_EYE_OUTER: 6,
  LEFT_EAR: 7, RIGHT_EAR: 8,
  MOUTH_LEFT: 9, MOUTH_RIGHT: 10,
  LEFT_SHOULDER: 11, RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13, RIGHT_ELBOW: 14,
  LEFT_WRIST: 15, RIGHT_WRIST: 16,
  LEFT_PINKY: 17, RIGHT_PINKY: 18,
  LEFT_INDEX: 19, RIGHT_INDEX: 20,
  LEFT_THUMB: 21, RIGHT_THUMB: 22,
  LEFT_HIP: 23, RIGHT_HIP: 24,
  LEFT_KNEE: 25, RIGHT_KNEE: 26,
  LEFT_ANKLE: 27, RIGHT_ANKLE: 28,
  LEFT_HEEL: 29, RIGHT_HEEL: 30,
  LEFT_FOOT_INDEX: 31, RIGHT_FOOT_INDEX: 32,
};

// Name → index mapping
const NAME_TO_INDEX = {};
Object.entries(LANDMARK).forEach(([name, idx]) => { NAME_TO_INDEX[name] = idx; });

/**
 * Mirror a landmark name LEFT↔RIGHT for bilateral support.
 */
export function mirrorLandmarkName(name) {
  if (name.startsWith('LEFT_')) return name.replace('LEFT_', 'RIGHT_');
  if (name.startsWith('RIGHT_')) return name.replace('RIGHT_', 'LEFT_');
  return name;
}

function resolveLandmark(landmarks, nameOrIdx) {
  const idx = typeof nameOrIdx === 'string' ? NAME_TO_INDEX[nameOrIdx] : nameOrIdx;
  const lm = landmarks[idx];
  return { x: lm.x, y: lm.y, z: lm.z || 0 };
}

function midpoint(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, z: (a.z + b.z) / 2 };
}

/**
 * Calculate angle in degrees at vertex formed by vectors vertex→a and vertex→b.
 * Returns value in [0, 180].
 */
export function calculateAngle(pointA, vertex, pointB) {
  const va = { x: pointA.x - vertex.x, y: pointA.y - vertex.y, z: pointA.z - vertex.z };
  const vb = { x: pointB.x - vertex.x, y: pointB.y - vertex.y, z: pointB.z - vertex.z };
  const dot = va.x * vb.x + va.y * vb.y + va.z * vb.z;
  const magA = Math.sqrt(va.x ** 2 + va.y ** 2 + va.z ** 2);
  const magB = Math.sqrt(vb.x ** 2 + vb.y ** 2 + vb.z ** 2);
  if (magA === 0 || magB === 0) return 0;
  const cosAngle = Math.max(-1, Math.min(1, dot / (magA * magB)));
  return (Math.acos(cosAngle) * 180) / Math.PI;
}

/**
 * Compute angle from a config { point_a, vertex, point_b } with side mirroring.
 */
export function getAngleForConfig(landmarks, angleConfig, side = 'left') {
  let paName = angleConfig.point_a;
  let vxName = angleConfig.vertex;
  let pbName = angleConfig.point_b;
  if (side === 'right') {
    paName = mirrorLandmarkName(paName);
    vxName = mirrorLandmarkName(vxName);
    pbName = mirrorLandmarkName(pbName);
  }
  return calculateAngle(
    resolveLandmark(landmarks, paName),
    resolveLandmark(landmarks, vxName),
    resolveLandmark(landmarks, pbName),
  );
}

// ---- Custom neck calculations ----
const TORSO_COMPENSATION_WEIGHT = 0.8;
const FLEXION_SENSITIVITY_GAIN = 1.3;
const FLEXION_HIGH_ANGLE_GAIN = 0.8;

function flexionGainAt(angleDeg) {
  const abs = Math.abs(angleDeg);
  if (abs <= 60) return FLEXION_SENSITIVITY_GAIN;
  if (abs >= 90) return FLEXION_HIGH_ANGLE_GAIN;
  const t = (abs - 60) / 30; // 60->0, 90->1
  return FLEXION_SENSITIVITY_GAIN - t * (FLEXION_SENSITIVITY_GAIN - FLEXION_HIGH_ANGLE_GAIN);
}

export function calculateNeckFlexion(landmarks) {
  const nose = resolveLandmark(landmarks, 'NOSE');
  const lShoulder = resolveLandmark(landmarks, 'LEFT_SHOULDER');
  const rShoulder = resolveLandmark(landmarks, 'RIGHT_SHOULDER');
  const lHip = resolveLandmark(landmarks, 'LEFT_HIP');
  const rHip = resolveLandmark(landmarks, 'RIGHT_HIP');
  const shoulderMid = midpoint(lShoulder, rShoulder);
  const hipMid = midpoint(lHip, rHip);
  const angle = calculateAngle(nose, shoulderMid, hipMid);
  let flexion = 180 - angle;
  if (nose.y < shoulderMid.y) flexion = -flexion;
  return flexion;
}

export function calculateNeckLateralFlexion(landmarks) {
  const lEar = resolveLandmark(landmarks, 'LEFT_EAR');
  const rEar = resolveLandmark(landmarks, 'RIGHT_EAR');
  const lShoulder = resolveLandmark(landmarks, 'LEFT_SHOULDER');
  const rShoulder = resolveLandmark(landmarks, 'RIGHT_SHOULDER');
  const earMid = midpoint(lEar, rEar);
  const shoulderMid = midpoint(lShoulder, rShoulder);
  const shoulderAngle = Math.atan2(rShoulder.y - lShoulder.y, rShoulder.x - lShoulder.x);
  const perpAngle = shoulderAngle - Math.PI / 2;
  const headAngle = Math.atan2(earMid.y - shoulderMid.y, earMid.x - shoulderMid.x);
  let diff = headAngle - perpAngle;
  while (diff > Math.PI) diff -= 2 * Math.PI;
  while (diff < -Math.PI) diff += 2 * Math.PI;
  return (diff * 180) / Math.PI;
}
export function calculateNeckSagittalSide(landmarks) {
  const { headAngle, torsoAngle } = calculateNeckFlexionComponents(landmarks);
  // Signed semantics retained for legacy/shared sagittal definition.
  const signed = (headAngle - TORSO_COMPENSATION_WEIGHT * torsoAngle) * 180 / Math.PI;
  return signed > 0 ? signed * flexionGainAt(signed) : signed;
}

export function calculateSagittalHeadTorsoAngles(landmarks) {
  // Robust side-view components for flexion quality checks:
  // 1) head angle from near-side face anchor (ear/eye midpoint) -> nose
  // 2) torso angle from near-side shoulder -> hip
  const nose = resolveLandmark(landmarks, 'NOSE');
  const lShoulder = resolveLandmark(landmarks, 'LEFT_SHOULDER');
  const rShoulder = resolveLandmark(landmarks, 'RIGHT_SHOULDER');
  const lHip = resolveLandmark(landmarks, 'LEFT_HIP');
  const rHip = resolveLandmark(landmarks, 'RIGHT_HIP');
  const lEar = resolveLandmark(landmarks, 'LEFT_EAR');
  const rEar = resolveLandmark(landmarks, 'RIGHT_EAR');
  const lEye = resolveLandmark(landmarks, 'LEFT_EYE');
  const rEye = resolveLandmark(landmarks, 'RIGHT_EYE');

  const leftSideFacing = (lShoulder.z || 0) <= (rShoulder.z || 0);
  const sign = leftSideFacing ? 1 : -1;
  const shoulder = leftSideFacing ? lShoulder : rShoulder;
  const hip = leftSideFacing ? lHip : rHip;
  const ear = leftSideFacing ? lEar : rEar;
  const eye = leftSideFacing ? lEye : rEye;

  // Blend ear+eye to reduce jitter while keeping the same clinical intent
  // as the existing nose-ear flexion signal.
  const faceAnchor = {
    x: (ear.x + eye.x) / 2,
    y: (ear.y + eye.y) / 2,
    z: (ear.z + eye.z) / 2,
  };

  const headDx = sign * (nose.x - faceAnchor.x);
  const headDy = faceAnchor.y - nose.y;
  const headAngleDeg = Math.atan2(headDx, headDy) * 180 / Math.PI;

  const torsoDx = leftSideFacing ? (hip.x - shoulder.x) : (shoulder.x - hip.x);
  const torsoDy = hip.y - shoulder.y;
  const torsoAngleDeg = Math.atan2(torsoDx, torsoDy) * 180 / Math.PI;

  return {
    headAngleDeg,
    torsoAngleDeg,
  };
}

function calculateNeckFlexionComponents(landmarks) {
  // Dedicated SIDE-VIEW flexion geometry.
  // Returns head and torso angles in radians.
  const nose = resolveLandmark(landmarks, 'NOSE');
  const lShoulder = resolveLandmark(landmarks, 'LEFT_SHOULDER');
  const rShoulder = resolveLandmark(landmarks, 'RIGHT_SHOULDER');
  const lEar = resolveLandmark(landmarks, 'LEFT_EAR');
  const rEar = resolveLandmark(landmarks, 'RIGHT_EAR');
  const leftSideFacing = (lShoulder.z || 0) <= (rShoulder.z || 0);
  const ear = leftSideFacing ? lEar : rEar;
  const sign = leftSideFacing ? 1 : -1;  // forward = +x left side, -x right side

  // Head angle from vertical using near-side ear→nose segment.
  const headDx = sign * (nose.x - ear.x);
  const headDy = ear.y - nose.y;   // positive when nose is above ear
  const headAngle = Math.atan2(headDx, headDy);

  // Torso inclination estimate from near-side shoulder/hip.
  const torsoAngle = (calculateTorsoInclinationSide(landmarks) * Math.PI) / 180;
  return { headAngle, torsoAngle };
}

function calculateNeckExtensionComponents(landmarks) {
  // Dedicated SIDE-VIEW extension geometry.
  // Kept separate from flexion path to avoid shared coupling.
  const nose = resolveLandmark(landmarks, 'NOSE');
  const lShoulder = resolveLandmark(landmarks, 'LEFT_SHOULDER');
  const rShoulder = resolveLandmark(landmarks, 'RIGHT_SHOULDER');
  const lEar = resolveLandmark(landmarks, 'LEFT_EAR');
  const rEar = resolveLandmark(landmarks, 'RIGHT_EAR');
  const leftSideFacing = (lShoulder.z || 0) <= (rShoulder.z || 0);
  const ear = leftSideFacing ? lEar : rEar;
  const sign = leftSideFacing ? 1 : -1;

  const headDx = sign * (nose.x - ear.x);
  const headDy = ear.y - nose.y;
  const headAngle = Math.atan2(headDx, headDy);

  const torsoAngle = (calculateTorsoInclinationSide(landmarks) * Math.PI) / 180;
  return { headAngle, torsoAngle };
}

export function calculateNeckFlexionSide(landmarks) {
  const { headAngle, torsoAngle } = calculateNeckFlexionComponents(landmarks);
  const signed = (headAngle - TORSO_COMPENSATION_WEIGHT * torsoAngle) * 180 / Math.PI;
  // Dedicated flexion metric: ignore extension direction.
  const flex = Math.max(0, signed);
  return flex * flexionGainAt(flex);
}
export function calculateNeckExtension(landmarks) {
  const { headAngle, torsoAngle } = calculateNeckExtensionComponents(landmarks);
  const signed = (headAngle - TORSO_COMPENSATION_WEIGHT * torsoAngle) * 180 / Math.PI;
  // Dedicated extension metric: ignore flexion direction.
  return Math.max(0, -signed);
}
export function calculateTorsoInclinationSide(landmarks) {
  // Torso inclination from SIDE VIEW: angle of the shoulder→hip vector from vertical.
  // Auto-detects camera side via shoulder z-depth.
  // Positive = forward lean, negative = backward lean, 0° = upright.
  const lShoulder = resolveLandmark(landmarks, 'LEFT_SHOULDER');
  const rShoulder = resolveLandmark(landmarks, 'RIGHT_SHOULDER');
  const lHip = resolveLandmark(landmarks, 'LEFT_HIP');
  const rHip = resolveLandmark(landmarks, 'RIGHT_HIP');
  const leftSideFacing = (lShoulder.z || 0) <= (rShoulder.z || 0);
  const shoulder = leftSideFacing ? lShoulder : rShoulder;
  const hip = leftSideFacing ? lHip : rHip;
  // Left-side: hip moves right (+x) for forward lean → positive.
  // Right-side: negate to keep positive = forward lean.
  const dx = leftSideFacing ? (hip.x - shoulder.x) : (shoulder.x - hip.x);
  const dy = hip.y - shoulder.y;   // positive since hip is below shoulder
  return Math.atan2(dx, dy) * 180 / Math.PI;
}

function resolveFaceLandmark(faceLandmarks, idx) {
  if (!Array.isArray(faceLandmarks) || idx == null || idx < 0 || idx >= faceLandmarks.length) return null;
  const lm = faceLandmarks[idx];
  if (!lm) return null;
  return { x: lm.x || 0, y: lm.y || 0, z: lm.z || 0 };
}

function calculateFaceYaw(faceLandmarks) {
  if (!Array.isArray(faceLandmarks) || faceLandmarks.length < 300) return null;
  const nose = resolveFaceLandmark(faceLandmarks, 1);
  const cheekL = resolveFaceLandmark(faceLandmarks, 234);
  const cheekR = resolveFaceLandmark(faceLandmarks, 454);
  const eyeL = resolveFaceLandmark(faceLandmarks, 33);
  const eyeR = resolveFaceLandmark(faceLandmarks, 263);
  if (!nose || !cheekL || !cheekR || !eyeL || !eyeR) return null;

  const dist2d = (a, b) => Math.hypot((a.x || 0) - (b.x || 0), (a.y || 0) - (b.y || 0));
  const dL = dist2d(nose, cheekL);
  const dR = dist2d(nose, cheekR);
  const cheekWidth = dist2d(cheekL, cheekR);
  if (dL <= 1e-6 || dR <= 1e-6 || cheekWidth <= 1e-6) return null;

  const ratio = Math.max(-0.92, Math.min(0.92, (dL - dR) / (dL + dR + 1e-6)));
  const yawFromRatio = Math.asin(ratio) * 180 / Math.PI;

  const yawFromZ = Math.atan2((cheekL.z - cheekR.z), cheekWidth + 1e-6) * 180 / Math.PI * 1.25;
  const yaw = yawFromZ * 0.65 + yawFromRatio * 0.35;

  const rollDeg = Math.atan2(eyeR.y - eyeL.y, eyeR.x - eyeL.x) * 180 / Math.PI;
  const cSize = Math.max(0, Math.min(1, cheekWidth / 0.18));
  const cRoll = 1 - Math.max(0, Math.min(1, Math.abs(rollDeg) / 35));
  const confidence = 0.6 * cSize + 0.4 * cRoll;

  if (!Number.isFinite(yaw) || !Number.isFinite(confidence)) return null;
  return { yaw, confidence };
}

export function calculateNeckRotation(landmarks, options = {}) {
  const nose = resolveLandmark(landmarks, 'NOSE');
  const lEar = resolveLandmark(landmarks, 'LEFT_EAR');
  const rEar = resolveLandmark(landmarks, 'RIGHT_EAR');
  const lShoulder = resolveLandmark(landmarks, 'LEFT_SHOULDER');
  const rShoulder = resolveLandmark(landmarks, 'RIGHT_SHOULDER');

  const shoulderMid = midpoint(lShoulder, rShoulder);
  const shoulderWidth = Math.abs(rShoulder.x - lShoulder.x);
  const earMid = midpoint(lEar, rEar);

  // Distance compensation: farther from camera -> smaller shoulder width -> weaker signals.
  // Keep this bounded so close-camera behavior stays stable.
  const refShoulderWidth = 0.20;
  const distanceGain = Math.max(1, Math.min(1.45, refShoulderWidth / (shoulderWidth || 1e-6)));
  const partialGain = 1 + (distanceGain - 1) * 0.5;

  // --- Method 1: ear z-depth differential ---
  const earDx = rEar.x - lEar.x;
  const earDz = rEar.z - lEar.z;
  const yawFromEars = Math.atan2(earDz, Math.abs(earDx) || 1e-6) * distanceGain;

  // --- Method 2: nose offset relative to ear midpoint ---
  let yawFromNose = 0;
  if (shoulderWidth > 1e-6) {
    const noseOffset = earMid.x - nose.x;
    const ratio = Math.max(-1, Math.min(1, (noseOffset / shoulderWidth) * 2 * partialGain));
    yawFromNose = Math.asin(ratio);
  }

  // --- Method 3: nose–shoulder-midpoint vertical triangle ---
  // Reduced gain (2.5 vs 3.5) to minimize camera-centering bias.
  let yawFromTriangle = 0;
  const dx = shoulderMid.x - nose.x;
  const dy = Math.abs(shoulderMid.y - nose.y);
  if (dy > 1e-6 && shoulderWidth > 1e-6) {
    const lateralRatio = dx / shoulderWidth;
    const verticalRatio = dy / shoulderWidth;
    const rotationEstimate = (lateralRatio / verticalRatio) * 2.5 * partialGain;
    yawFromTriangle = Math.atan(rotationEstimate);
  }

  // Blend methods 1-3 (rebalanced to reduce directional bias from Method 3)
  const blend123 = yawFromEars * 0.35 + yawFromNose * 0.30 + yawFromTriangle * 0.35;

  // --- Method 4: opposite ear approaching nose ---
  // Use a single sign-symmetric formulation so left/right rotations are
  // measured with identical magnitude logic (only sign differs).
  // Positive = left rotation, negative = right rotation.
  const rEarDist = Math.abs(rEar.x - nose.x);
  const lEarDist = Math.abs(lEar.x - nose.x);
  const earBaseline = Math.max(rEarDist, lEarDist, shoulderWidth * 0.05);

  let yawFromCrossing = 0;
  const sideDelta = (lEarDist - rEarDist) / (earBaseline || 1e-6);
  const sideMag = Math.abs(sideDelta);

  // Equivalent to requiring one ear to be meaningfully closer (was ~0.85 ratio gate).
  if (sideMag > 0.10) {
    const prog = Math.max(0, sideMag * partialGain);
    if (prog > 0.10) {
      const p = Math.min(prog, 1.3);
      let mag = Math.asin(Math.min(p, 1));
      if (p > 1) mag += (p - 1) * (30 * Math.PI / 180);
      yawFromCrossing = Math.sign(sideDelta) * mag;
    }
  }

  // Smoothly blend method 4 in (instead of hard switching) to avoid 15-50 deg jumps.
  const crossMag = Math.abs(yawFromCrossing);
  const blendMag = Math.abs(blend123);
  const blendDeg = blendMag * 180 / Math.PI;

  const blendStartDeg = 12;
  const blendFullDeg = 28;
  let w = Math.max(0, Math.min(1, (blendDeg - blendStartDeg) / (blendFullDeg - blendStartDeg)));

  // If crossing already indicates higher rotation, let it contribute earlier
  // to reduce high-angle under-reading.
  const crossDeg = crossMag * 180 / Math.PI;
  const wCross = Math.max(0, Math.min(1, (crossDeg - 20) / 22));
  w = Math.max(w, wCross);

  // If estimators disagree on direction, trust blend123 near neutral and
  // allow more crossing influence at higher magnitudes.
  if (crossMag > 0 && blendMag > 0 && Math.sign(yawFromCrossing) !== Math.sign(blend123)) {
    const disagreeCap = blendDeg >= 45 ? 0.95 : (blendDeg >= 30 ? 0.55 : 0.35);
    w = Math.min(w, disagreeCap);
  }
  const result = blend123 * (1 - w) + yawFromCrossing * w;

  let deg = (result * 180) / Math.PI;

  // Apply a tapering offset: 3 deg at low angles, fading to 0 deg by 70 deg.
  const absDeg = Math.abs(deg);
  let offset = 3;
  if (absDeg > 50) {
    const t = Math.min(1, (absDeg - 50) / 20); // 50->0, 70->1
    offset = 3 * (1 - t);
  }
  const sign0 = Math.sign(deg) || 1;
  deg = sign0 * Math.max(0, absDeg - offset);

  // Low-angle sensitivity boost (0-30 deg): stronger near neutral,
  // tapering back to 1.0 by 18 deg.
  const absPostOffset = Math.abs(deg);
  if (absPostOffset > 0 && absPostOffset < 18) {
    const t = absPostOffset / 18; // 0->1
    const lowAngleGain = 1.28 - 0.28 * t; // 1.28 at 0deg, 1.0 at 18deg
    deg *= lowAngleGain;
  }

//  // Soft compression above 50 deg to correct high-angle overshoot
//  const knee = 50;
//  const factor = 0.90;
//  if (Math.abs(deg) > knee) {
//    const sign = Math.sign(deg);
//    deg = sign * (knee + (Math.abs(deg) - knee) * factor);
//  }
//
//  // Additional high-angle calibration above 65 deg:
//  // e.g. ~85 deg measured -> ~75 deg corrected.
//  const highKnee = 65;
//  const highFactor = 0.2;
//  if (Math.abs(deg) > highKnee) {
//    const sign = Math.sign(deg);
//    deg = sign * (highKnee + (Math.abs(deg) - highKnee) * highFactor);
//  }
//
//  

  const faceYawData = calculateFaceYaw(options.faceLandmarks);
  if (options.faceOnly && faceYawData) {
    return faceYawData.yaw;
  }

  // Optional pose+face fusion for neck rotation when face landmarks are available.
  if (faceYawData) {
    const yPose = deg;
    const yFace = faceYawData.yaw;
    const cFace = faceYawData.confidence;
    const absPose = Math.abs(yPose);

    let wBase;
    if (absPose <= 15) {
      wBase = 0.72;
    } else if (absPose <= 35) {
      wBase = 0.72 - ((absPose - 15) / 20) * 0.10;
    } else if (absPose <= 90) {
      wBase = 0.62 - ((absPose - 35) / 55) * 0.12;
    } else {
      wBase = 0.50;
    }

    let wFace = wBase * (0.55 + 0.45 * cFace);
    wFace = Math.max(0.25, Math.min(0.82, wFace));

    const delta = Math.abs(yFace - yPose);
    const signMismatch = Math.sign(yFace) !== Math.sign(yPose);
    const maxMag = Math.max(Math.abs(yFace), Math.abs(yPose));

    if (delta > 22 && cFace < 0.55) {
      wFace *= 0.35;
    }
    if (signMismatch && maxMag < 18) {
      wFace = Math.min(wFace, 0.25);
    }
    if (signMismatch && maxMag >= 45) {
      wFace = Math.max(wFace, 0.55);
    }

    // At higher angles, under-confident face yaw can under-read due to partial
    // occlusion. Bias slightly toward pose/crossing when signals diverge.
    if (absPose >= 55 && cFace < 0.75) {
      wFace *= 0.72;
    }
    if (absPose >= 45 && (Math.abs(yPose) - Math.abs(yFace)) > 10) {
      wFace *= 0.65;
    }
    if (absPose >= 70 && cFace < 0.8) {
      wFace = Math.min(wFace, 0.35);
    }

    deg = (1 - wFace) * yPose + wFace * yFace;
  }

  return deg;
}

const CUSTOM_CALCULATORS = {
  neck_flexion: calculateNeckFlexion,
  neck_flexion_side: calculateNeckFlexionSide,
  neck_lateral_flexion: calculateNeckLateralFlexion,
  neck_rotation: calculateNeckRotation,
  neck_sagittal_side: calculateNeckSagittalSide,
  neck_extension: calculateNeckExtension,
  torso_inclination_side: calculateTorsoInclinationSide,
};

/**
 * High-level angle computation for a single frame.
 * Uses custom calculator if landmarkConfig has calculation_type,
 * otherwise standard 3-point angle.
 */
export function computeAngleForFrame(landmarks, landmarkConfig, side = 'left', options = {}) {
  const calcType = landmarkConfig.calculation_type;
  if (calcType && CUSTOM_CALCULATORS[calcType]) {
    return CUSTOM_CALCULATORS[calcType](landmarks, options);
  }
  return getAngleForConfig(landmarks, landmarkConfig.primary_angle, side);
}

/**
 * Check compensation in a single frame.
 * Returns array of violation objects.
 */
export function checkCompensation(landmarks, compensationChecks, side = 'left') {
  if (!compensationChecks) return [];
  const violations = [];
  for (const check of compensationChecks) {
    const angle = getAngleForConfig(landmarks, check, side);
    const maxDev = check.max_deviation_degrees || 10;
    const deviation = Math.abs(180 - angle);
    if (deviation > maxDev) {
      violations.push({
        name: check.name,
        angle: Math.round(angle * 100) / 100,
        deviation: Math.round(deviation * 100) / 100,
        threshold: maxDev,
      });
    }
  }
  return violations;
}
